/* eslint-disable react/require-default-props */
import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import textHelper from '@utils/textHelper';
import Paper from '@base/Paper/Paper';
import P2 from '@base/P2/P2';
import Link from '@base/Link/Link';
import H5 from '@base/H5/H5';
import {
  SEE_ALL_CLICKED,
  GOAL_PAGE,
  SYLLABUS_CARD_CLICKED,
  SYLLABUS_TAB_CLICKED
} from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import Image from '@base/Image';
import { segment } from '@actions/analytics';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { getTopologyName } from '@selectors/topologies';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';
import CardCarousel from './CardCarousel';

const SecWrapper = styled.section`
  position: relative;
  margin: auto;
  margin-top: 120px;
  padding-bottom: 32px;
  width: 1376px;
  overflow: hidden;
  @media (max-width: 70.95em) {
    width: 728px;
  }
  @media (max-width: 47.95em) {
    width: 100%;
    margin-top: 56px;
    padding: 0;
  }
`;

const CardContainer = styled(Paper)`
  width: 282px;
  height: 100%;
  margin-right: ${Spacings.SPACING_6B};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  padding-top: ${Spacings.SPACING_6B};
  padding-bottom: ${Spacings.SPACING_6B};
  padding-left: 16px;
  padding-right: ${Spacings.SPACING_6B};
  cursor: pointer;
  @media (max-width: 47.95em) {
    margin-right: 16px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  height: inherit;
  flex: 1;
`;

const CardTop = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: ${Spacings.SPACING_4B};
`;

const TitleBlock = styled.div`
  margin-left: ${Spacings.SPACING_4B};
`;

const CoursesCount = styled(P2)`
  color: var(--color-text-primary);
  margin-top: ${Spacings.SPACING_1B};
`;

const IconWrapper = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  margin-right: ${Spacings.SPACING_3B};
`;

const BenefitsSection = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_3B};
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    margin-left: ${Spacings.SPACING_4B};
    align-items: start;
  }
`;

const BenefitsWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_3B};
`;

const BenefitText = styled(H5)`
  font-weight: ${Fonts.LIGHT};
`;

const AppMessageWrapper = styled.div`
  width: 1136px;
  margin: auto;
  display: flex;
  align-items: center;
  margin-top: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    margin-left: ${Spacings.SPACING_4B};
    width: 100%;
  }
`;

const RECORDED_GOAL_BENEFITS = [
  '100+ video lessons',
  'Recorded by top educators',
  'Organised by topics and chapters'
];

const Card = ({
  topology,
  goalUID,
  goalName,
  goalSlug,
  dataAnalytics,
  dispatch,
  index,
  label,
  isRecordedGoal
}) => {
  const { uid, name, count: plusCourseCount, color, children } = topology;
  const asPath = `/goal/${goalSlug}/${goalUID}/topic/${uid}`;
  let renderSecondaryCoursesCount = '';
  renderSecondaryCoursesCount += textHelper.pluralize(
    plusCourseCount,
    isRecordedGoal ? 'video lesson' : 'course'
  );

  const cardClickAnalytics = () => {
    segment.track(SYLLABUS_CARD_CLICKED, {
      subscription_duration: 'NA',
      subscription_end_date: 'NA',
      is_subscription_active: false,
      last_primary_source: 'Goal landing page',
      last_primary_source_section: label,
      goal_uid: goalUID,
      goal_name: goalName,
      topology_id: topology.uid,
      topology_name: topology.name,
      topology_level: 30
    });
  };

  const icon = `subjects-icons/Type-${name.slice(0, 1)}.svg`;
  return (
    <Link
      href="/goal/[goalSlug]/[goalUID]/topic/[topicUid]"
      as={asPath}
      onClick={cardClickAnalytics}
    >
      <CardContainer color={color}>
        <Content>
          <CardTop>
            <Image src={icon} width={40} height={40} alt="subject-icon" />
            <TitleBlock>
              <H5 data-analytics={dataAnalytics}>{name}</H5>
              <CoursesCount>{renderSecondaryCoursesCount}</CoursesCount>
            </TitleBlock>
          </CardTop>

          <P2 color="var(--color-text-secondary)">{children}</P2>
        </Content>
      </CardContainer>
    </Link>
  );
};

const RecordedGoalBenefits = () => {
  return (
    <BenefitsSection>
      {RECORDED_GOAL_BENEFITS.map((benefit, index) => {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <BenefitsWrapper key={index}>
            <IconWrapper>
              <Image
                src="/gtp/benefit-point-icon.svg"
                alt="benefit"
                layout="fill"
              />
            </IconWrapper>
            <BenefitText>{benefit}</BenefitText>
          </BenefitsWrapper>
        );
      })}
    </BenefitsSection>
  );
};

const RecordedGoalAppMessage = () => {
  return (
    <AppMessageWrapper>
      <IconWrapper>
        <Image src="/gtp/app.svg" alt="benefit" layout="fill" />
      </IconWrapper>
      <H5>Lessons are only available over the Android app</H5>
    </AppMessageWrapper>
  );
};

type TopicsSectionProps = {
  block: {
    data: any[];
  };
  goalUID: string;
  goalSlug: string;
  type: string;
  href?: string;
  as?: string;
};

const TopicsSection: FC<TopicsSectionProps> = ({
  block,
  goalUID,
  goalSlug,
  href,
  as,
  type
}) => {
  const { data: topologies } = block;
  const dispatch = useDispatch();
  const goalEvent = useGoalEventProps({ goalUID });

  const goalName = useSelector((state) => getTopologyName(state, goalUID));
  const isRecordedGoal = useIsRecordedGoal(goalUID);

  const label = isRecordedGoal
    ? 'Comprehensive video lessons'
    : 'Comprehensive syllabus';

  const getCarouselSteps = () => {
    if (topologies.length >= 4) return 4;
    return 3;
  };

  const seeAllClickEvent = () => {
    segment.track(SEE_ALL_CLICKED, {
      ...goalEvent,
      last_primary_source: GOAL_PAGE,
      last_primary_source_section: label
    });

    segment.track(SYLLABUS_TAB_CLICKED, {
      subscription_duration: 'NA',
      subscription_end_date: 'NA',
      is_subscription_active: false,
      last_primary_source: 'Goal landing page',
      last_primary_source_section: label,
      goal_uid: goalUID,
      goal_name: goalName
    });
  };

  if (topologies.length === 0) return null;
  return (
    <SecWrapper>
      <CardCarousel
        label={label}
        subLabel="Our courses provide complete coverage of the topics you need to be prepared for"
        step={getCarouselSteps()}
        href={href}
        as={as}
        dataAnalytics={type}
        type="topics"
        handleSeeAll={seeAllClickEvent}
        extraBenefits={isRecordedGoal && <RecordedGoalBenefits />}
      >
        {topologies.map((topology, index) => (
          <Card
            key={topology.uid}
            topology={topology}
            goalUID={goalUID}
            goalSlug={goalSlug}
            goalName={goalName}
            dispatch={dispatch}
            dataAnalytics="subscription-topic"
            index={index}
            label={label}
            isRecordedGoal={isRecordedGoal}
          />
        ))}
      </CardCarousel>
      {isRecordedGoal && <RecordedGoalAppMessage />}
    </SecWrapper>
  );
};

export default TopicsSection;
